import * as React from "react";
import {
  BILLABLE,
  BUDGET,
  CONFIG,
  DASHBOARD,
  EXTENSIONS,
  MILESTONE,
  TIMESHEET_CALENDAR,
  USERS,
  SURVEY,
  ANALYTICS,
  ALLOCATION,
  PUZZLE,
} from "icons";
import { JobProvider, useJob } from "@/modules/work/jobs/context";
import { Outlet } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarProvider,
  SidebarRail,
} from "ui/components/sidebar";
import Loader from "@/components/loader";
import { NavItem } from "@/components/nav/item";
import { NavItemWithBadge } from "@/components/nav/nav-item-with-badge";
import { NavSubItem } from "@/components/nav/sub-item";
import { PermissionGate } from "permissions/components/gate";
import { JobHeader } from "@/modules/work/jobs/components/header";
import { CollapsibleJobHeader } from "./components/collapsible-header";
import { PAGE_BANNER_PORTAL_ID } from "utils";

export default function JobLayout({ ...props }) {
  return (
    <JobProvider>
      <JobLayoutContent {...props} />
    </JobProvider>
  );
}
function JobLayoutContent(props) {

  const {
    job,
    jobId,
    config,
    isLoading,
    type,
  } = useJob();

  const jobtype = job?.type || type;

  if (isLoading) {
    return <Loader />;
  }

  if (!jobtype) return null;

  return (
    <div className="flex-1 relative min-w-0">
      <JobHeader />
      <div className="flex flex-1 relative min-w-0">
        <SidebarProvider
          className="w-fit overflow-hidden max-h-dvh"
          name="project"
        >
          <Sidebar
            collapsible="icon"
            {...props}
            className="sidebar-height border-r left-auto inset-y-auto top-0 mt-28"
          >
            <SidebarHeader className="p-0">
              <div className="p-4">
                <CollapsibleJobHeader job={job} isLoading={isLoading} />
              </div>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarMenu>
                  <NavItem
                    title="Dashboard"
                    icon={DASHBOARD}
                    url={`/work/jobs/${jobId}`}
                    matchExact={true}
                  />
                  <NavItem
                    title="Analytics"
                    icon={ANALYTICS}
                    url={`/work/jobs/${jobId}/analytics`}
                    matchExact={true}
                  >
                    {/* <NavSubItem
                      title="Overview"
                      url={`/work/jobs/${jobId}/analytics/overview`}
                    /> */}
                    <NavSubItem
                      title="Budget"
                      url={`/work/jobs/${jobId}/analytics/budget`}
                    />
                  </NavItem>
                  <NavItemWithBadge
                    title="Milestones"
                    icon={MILESTONE}
                    url={`/work/jobs/${jobId}/milestones`}
                    collection="milestones"
                    filters={{ job: { _eq: jobId }, status: { _eq: "in_progress" } }}
                  />
                  {jobtype !== "free" && (
                    <NavItemWithBadge
                      title="Billables"
                      icon={BILLABLE}
                      url={`/work/jobs/${jobId}/billables`}
                      collection="billables"
                      filters={{ job: { _eq: jobId }, status: { _nin: ["cancelled", "collected", "archived"] } }}
                      requiredPermissions={{
                        type: "collection",
                        collection: "billables",
                      }}
                    />
                  )}
                  <NavItem
                    title="Timesheet"
                    icon={TIMESHEET_CALENDAR}
                    url={`/work/jobs/${jobId}/timesheet`}
                    matchExact={true}
                  >
                    <NavSubItem
                      title="Employees"
                      url={`/work/jobs/${jobId}/timesheet/employees`}
                    />
                    <NavSubItem
                      title="Departments"
                      url={`/work/jobs/${jobId}/timesheet/departments`}
                    />
                    <NavSubItem
                      title="Tags"
                      url={`/work/jobs/${jobId}/timesheet/tags`}
                    />
                    <NavSubItem
                      title="Logs"
                      url={`/work/jobs/${jobId}/timesheet/logs`}
                    />
                    {/* <NavSubItem
                      title="History"
                      url={`/work/jobs/${jobId}/timesheet/history`}
                    /> */}
                  </NavItem>
                  {jobtype !== "free" && (
                    <NavItem
                      title={
                        <span className="flex items-center justify-between">
                          Config
                          {!config && !isLoading && (
                            <PermissionGate
                              rule={"create:SC,AM"}
                              collection="dedicated_job_config"
                            >
                              <span className="ml-2 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            </PermissionGate>
                          )}
                        </span>
                      }
                      icon={CONFIG}
                      url={`/work/jobs/${jobId}/config`}
                      requiredPermissions={{
                        type: "key",
                        collection: `${jobtype}_job_config`,
                        key: "currency"
                      }}
                    />
                  )}
                  <NavItem
                    title="Allocation"
                    icon={ALLOCATION}
                    url={`/work/jobs/${jobId}/allocation`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "billables",
                    }}
                  />
                  <NavItem
                    title="Extensions"
                    icon={EXTENSIONS}
                    url={`/work/jobs/${jobId}/extensions`}
                  />
                  <NavItem
                    title="Employees"
                    icon={USERS}
                    url={`/work/jobs/${jobId}/employees`}
                  />
                  <NavItem
                    title="Surveys"
                    icon={SURVEY}
                    url={`/work/jobs/${jobId}/surveys`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "surveys",
                    }}
                  />
                  <NavItem
                    title="Challenges"
                    icon={PUZZLE}
                    url={`/work/jobs/${jobId}/challenges`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "job_challenges",
                    }}
                  />
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
            <SidebarRail />
          </Sidebar>
        </SidebarProvider>
        <div className="flex-1 overflow-auto min-w-0">
          {/* Page-level banners (portaled in); collapses when empty. */}
          <div
            id={PAGE_BANNER_PORTAL_ID}
            className="flex flex-col gap-2 px-4 pt-4 empty:hidden"
          />
          <Outlet />
        </div>
      </div>
    </div>
  );
}
